import { Fragment } from 'react';
import Link from 'next/link';
import useCaro from '~/hooks/useCaro';
import SiteSettings from '~/siteSettings';
import { min2secs, isGiveAwayLink } from '~/utils/utils';
import VideoPreview from '../videoPreview/videoPreview';
import LinkTracker from '~/components/linkTracker/linkTracker';

/**
 * Content Carousel
 * @category Component
 * @extends Component
 * @summary Displays the content carousel block.
 * @prop {Object} items - The block items.
 * @prop {Object} title - The block title.
 * @prop {bool} hasLoadMore - Does the item have load more.
 * @prop {function} loadMore - Function to call if user loads more.
 * @prop {bool} loading - Loading state.
 * @prop {int} count - Total result count.
 * @prop {bool} hideTypeIcons - Should we hide the type icons?.
 * @prop {string} imgStyleLg - If provided, use this image style in lieu of the LG default.
 * @prop {string} imgStyleSm - If provided, use this image style in lieu of the SM default.
 * @prop {string} loop - Should the carousel loop? Accepted values 'yes' & 'no'.
 * @prop {function} headingOveride - If provided, this function should return the custom heading override.
 * @return {Node} React node containing the content carousel.
 */

const YoutubeCarouselContent = (props) => {
  const { items, title, imgStyleLg, imgStyleSm, loop, headingOveride } = props;
  const { viewportRef } = useCaro({
    loop: loop === 'yes',
    align: 'start'
  });

  // Assign Image Styles so we can interpolate
  const _imgStyleLg = imgStyleLg || 'lg';
  const _imgStyleSm = imgStyleSm || 'sm';

  return (
    <div className='x-hidden wrap-relative'>
      <div className='constrain'>
        <div className='carousel-content'>
          { headingOveride ? headingOveride() :
            <h2 className='carousel-content__heading'>
              { title }
            </h2>
          }
          <div className='carousel-content__viewport' ref={viewportRef}>
            <div className='carousel-content__container'>
              {items.map((slide, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={`content-caro-${ slide?.nid }-${ i }`}>
                  { slide?.path &&
                    <div className='card'>
                      <LinkTracker
                        destinationUrl={ slide?.path }
                        linkCta={ slide?.title.toLowerCase() }
                        linkFormat='tile'
                        linkRegion={ title }
                        linkSubRegion={`carousel - ${i + 1}`}
                      >
                        <Link legacyBehavior href={ slide?.path }>
                          <a
                            aria-label={`Visit ${ slide?.title }`}
                            data-autotrack='false'
                            target={ isGiveAwayLink(slide?.path) ? '_blank' : '_self'}
                          >
                            <div className='placed'>
                              { slide?.field_image?.field_image && slide?.__typename === 'video' &&
                                <VideoPreview item={ slide }>
                                  <picture>
                                    <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ slide?.field_image?.field_image?.[_imgStyleSm]?.url } />
                                    <img
                                      src={ slide?.field_image?.field_image?.[_imgStyleLg]?.url }
                                      alt={ slide?.field_image?.alt }
                                      width={ slide?.field_image?.field_image?.[_imgStyleLg]?.width }
                                      height={ slide?.field_image?.field_image?.[_imgStyleLg]?.height }
                                      className='card__img placed-elem'
                                      loading='lazy'
                                    />
                                  </picture>
                                </VideoPreview>
                              }
                              { (slide?.field_image || slide?.field_secondary_image) && slide?.__typename !== 'video' &&
                                <picture>
                                  <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ slide?.field_secondary_image ? slide?.field_secondary_image?.field_image?.field_image?.sm?.url : slide?.field_image?.field_image?.sm?.url } />
                                  <img
                                    src={ slide?.field_secondary_image ? slide?.field_secondary_image?.field_image?.field_image?.lg?.url : slide?.field_image?.field_image?.lg?.url }
                                    alt={ slide?.field_secondary_image ? slide?.field_secondary_image?.field_image?.alt : slide?.field_image?.alt }
                                    width={ slide?.field_secondary_image ? slide?.field_secondary_image?.field_image?.field_image?.width : slide?.field_image?.field_image?.width }
                                    height={ slide?.field_secondary_image ? slide?.field_secondary_image?.field_image?.field_image?.height : slide?.field_image?.field_image?.height }
                                    className='card__img placed-elem'
                                    loading='lazy'
                                  />
                                </picture>
                              }
                              <div className='placed-275x154'></div>
                              { slide?.field_video_mpx_id && slide?.field_video_mpx_id?.duration !== undefined &&
                                <div className='block-videos__duration'>
                                  { min2secs(slide?.field_video_mpx_id?.duration) }
                                </div>
                              }
                            </div>
                            <h3>
                              { slide?.title }
                            </h3>
                          </a>
                        </Link>
                      </LinkTracker>
                    </div>
                  }
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default YoutubeCarouselContent;
